import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';

import Utils from '../utils';
import { PrintOnEnum, PrintOnOptions } from '../data/printOn';
import { LabelPreviewHelper, SheetType } from '../data/print';
import ModalModule from '../modal';
import { WrappedButton, WrappedButtonType } from '../ui/button';
import { PrintOnFieldWithLabel } from '../ui/field';
import { WrappedInputTextWithLabel, WrappedInputNumberWithLabel } from '../ui/input';
import LabelPreview from './LabelPreview.component';

const { Modal, ModalButtonActionType } = ModalModule;
const { withLayoutAndModalContexts } = Utils;

// Use JavaScript Intl.NumberFormat to format per-print limit value
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

class PrintModal extends Component {
	constructor(props) {
		super(props);

		const {
			data: { filteredPrintOnOptions, selectedOption, netstampCount },
		} = props;

		const defaultPrintOn = selectedOption || PrintOnEnum.ShippingLabel812x11;
		const defaultSheet = LabelPreviewHelper.getSheetType(defaultPrintOn);

		this.state = {
			printOn: defaultPrintOn,
			qty: netstampCount || 1,

			// default to all PrintOnOptions if restricted options are not provided
			printOnOptions: filteredPrintOnOptions || PrintOnOptions,
			nsSerial: '',

			// calculate the default sheet value based on the selected print-on value
			sheet: defaultSheet,
			series: this.updateSeries(defaultPrintOn),
			history: [],

			startPosition: 1,
			sheetData: {},

			isValid: true,
			errorMessage: '',
			isPrintButtonDisabled: defaultSheet === SheetType.NetStamps, // disable reprint button initially for netstamps
		};
	}

	updateSeries = printOn => {
		const {
			data: { carrier },
		} = this.props;
		const sheet = LabelPreviewHelper.getSheetType(printOn);

		if (sheet === SheetType.ShippingLabel || sheet === SheetType.Roll) {
			return carrier;
		} else if (sheet === SheetType.NetStamps) {
			return '';
		} else {
			return LabelPreviewHelper.getSeries(printOn);
		}
	};

	onPrintOnChange = ({ value }) => {
		const sheet = LabelPreviewHelper.getSheetType(value);
		const series = this.updateSeries(value);

		this.setState({
			printOn: value,
			sheet,
			series,
		});
	};

	// Notify the caller the print ("confirm") action.
	onPrintClick = async () => {
		const {
			modalId,
			modalContext,
			data: { onReprintClick },
		} = this.props;
		const { sheet, startPosition, printOn, sheetData, series } = this.state;

		this.setState({
			isPrintButtonDisabled: true,
		});

		if (isFunction(onReprintClick)) {
			await onReprintClick(startPosition, printOn, sheetData, series, sheet);

			this.setState({
				isPrintButtonDisabled: false,
			});
		}

		modalContext.closeModal(modalId, ModalButtonActionType.Confirm, {
			sheet,
			startPosition,
		});
	};

	onLabelPreviewSelect = ({ startPosition, qty, isValid = true, errorMessage = '' }) => {
		const { sheet } = this.state;
		const {
			data: { isReprint },
		} = this.props;
		const isShippingLabel = sheet === SheetType.Label;

		// do not allow the user to select a starting slot for a shipping label reprint
		if (isShippingLabel && isReprint) {
			return;
		}

		this.setState({
			startPosition,
			qty,
			isValid,
			errorMessage,
		});
	};

	onChangeNsSerial = ({ target: { value } }) => {
		const { series, sheet, qty } = this.state;
		const {
			data: { isReprint, isSeriesValid, isNetStampsLayoutTypeValid },
		} = this.props;
		const sheetData = LabelPreviewHelper.getData(sheet, value);
		const isNetStamps = sheet === SheetType.NetStamps;
		let newSeries = isString(sheetData?.type) ? value : series;
		let isValid = true;
		let errorMessage = '';

		if (isFunction(isNetStampsLayoutTypeValid) && isNetStamps && newSeries) {
			({ isValid, errorMessage } = isNetStampsLayoutTypeValid(sheet, newSeries));
			newSeries = isValid ? newSeries : '';
		}

		if (isFunction(isSeriesValid) && isNetStamps && newSeries) {
			({ isValid, errorMessage } = isSeriesValid(sheet, newSeries));
			newSeries = isValid ? newSeries : '';
		}

		this.setState({
			nsSerial: isValid ? value : '',
			series: newSeries,
			startPosition: 1,
			qty: isReprint ? qty : 1,
			sheetData,
			isValid,
			errorMessage,
			isPrintButtonDisabled: isNetStamps && (!sheetData || !isValid), // enable print button if the serial number is valid and sheet data is available
		});
	};

	renderNsQuantity = () => {
		const {
			data: { isReprint },
		} = this.props;
		const { qty } = this.state;

		if (isReprint) {
			return <div className="quantity-display">Quantity: {qty}</div>;
		}

		return (
			<div className="quantity-input">
				<WrappedInputNumberWithLabel
					label="Quantity:"
					uiElementConfig={{
						value: qty,
						showButtons: true,
						min: 0,
						dataqa: 'print-nsquantity',
						onChange: ({ target: { value } }) => {
							this.setState({
								qty: value,
							});
						},
					}}
				/>
			</div>
		);
	};

	render() {
		const { modalId, data } = this.props;
		const { header, labels, totalCost = 0, isReprint, isPositionValid } = data || {};
		const {
			printOn,
			printOnOptions,
			history,
			sheet,
			nsSerial,
			series,
			startPosition,
			qty,
			isValid,
			errorMessage,
			isPrintButtonDisabled,
		} = this.state;
		const isNetStamps = sheet === SheetType.NetStamps;

		return (
			<Modal
				modalId={modalId}
				className="common-print-modal"
				dataqa={`${isReprint ? 're' : ''}print-${
					isNetStamps ? 'netstamps' : 'label'
				}-modal-header`}
				header={header}
				hideFooter
			>
				{/* custom body */}
				<div className="dialog-body">
					<div className="p-grid p-nogutter">
						<PrintOnFieldWithLabel
							className="p-col-12"
							label="Printing on:"
							uiElementConfig={{
								name: 'printOn',
								value: printOn,
								options: printOnOptions,
								onChange: this.onPrintOnChange,
								dataqa: `${isReprint ? 're' : ''}print-${
									isNetStamps ? 'netstamps' : 'label'
								}-modal-print-on-dropdown`,
							}}
						/>
						<hr />
						{isNetStamps && (
							<div className="p-col-12">
								<div style={{ width: 250 }}>
									<WrappedInputTextWithLabel
										label="Serial #:"
										isInputInvalid={!isValid}
										errorMessage={errorMessage}
										uiElementConfig={{
											name: 'nsSerial',
											value: nsSerial,
											onChange: this.onChangeNsSerial,
											dataqa: `${
												isReprint ? 're' : ''
											}print-netstamps-modal-serial-number`,
										}}
									/>
								</div>
							</div>
						)}
						<div className="p-col-12 label-preview">
							<div className="field-label">Starting Label:</div>
							<div className="label-preview-wrapper">
								<LabelPreview
									showRequireSheetTip
									sheet={sheet}
									series={series}
									startPosition={startPosition}
									labels={labels}
									history={history}
									qty={qty}
									onSelect={this.onLabelPreviewSelect}
									isPositionValid={isPositionValid}
									isReprint={isReprint}
								/>
							</div>
						</div>

						{isNetStamps && <>{this.renderNsQuantity()}</>}
					</div>
				</div>

				{/* custom Footer */}
				<div className="p-dialog-footer custom-footer">
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						label={isReprint ? 'Reprint' : 'Print'}
						onClick={this.onPrintClick}
						disabled={isPrintButtonDisabled}
						dataqa={`${isReprint ? 're' : ''}print-${
							isNetStamps ? 'netstamps' : 'label'
						}-modal-reprint-button`}
					/>
					<div className="total-cost-text">
						Total Cost: {usCurrencyFormatter.format(totalCost)}
					</div>
					{!isReprint && (
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							label="Print Sample"
							onClick={this.onPrintClick}
							dataqa={`print-${
								isNetStamps ? 'netstamps' : 'label'
							}-modal-print-sample`}
						/>
					)}
				</div>
			</Modal>
		);
	}
}

PrintModal.propTypes = {
	modalId: PropTypes.number,
	data: PropTypes.shape({
		// Modal title
		header: PropTypes.string,

		// Label Image Url(s)
		labels: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

		// Total print cost
		totalCost: PropTypes.number,

		// Carrier enum value
		carrier: PropTypes.string,
	}),
};

export default withLayoutAndModalContexts(PrintModal);
